import type { TaskRecord } from '../../containers/tasks-table/tasks-table.type';
import type { AgentSource } from '../events-client/events-client.type';
import type { TimeAllocationType } from '../work-periods-client/work-periods-client.type';

type MeasureValues = Record<string, number | null>;

type CycleTimeStats = {
  median_cycle_time_days: number | null;
  average_cycle_time_days: number | null;
  total_cycle_time_days: number | null;
  total_tasks: number;
};

type CycleTimeHistogramBucket = {
  bucket_days: string;
  agent_count: number;
  non_agent_count: number;
};

type CycleTimeAnalysis = {
  agent: CycleTimeStats;
  non_agent: CycleTimeStats;
  histogram: CycleTimeHistogramBucket[];
};

type AgentAdoption = {
  active_agent_users: MeasureValues;
  eligible_users: MeasureValues;
  agent_adoption_rate: MeasureValues;
};

type AgentDashboardResponse = {
  throughput: MeasureValues;
  agent_cycle_time: MeasureValues;
  non_agent_cycle_time: MeasureValues;
  agent_throughput_ratio: MeasureValues;
  agent_session_count_overtime: MeasureValues;
  agent_event_count_overtime: MeasureValues;
  ai_code_percentage: MeasureValues;
  cycle_time_analysis: CycleTimeAnalysis;
  agent_adoption: AgentAdoption;
};

type AgentDashboardParams = {
  organization_id: string;
  start_date: string;
  end_date: string;
  portfolio_id: string;
  force_recalculation?: boolean;
  time_allocation_type?: string;
  /**
   * Optional single work-item type filter applied to the task-level metrics
   * (cycle time, throughput). Omitted when "All work item types" is selected.
   */
  task_type?: string;
  /**
   * Optional story-point filter applied to the task-level metrics (cycle time,
   * throughput). A number matches that exact estimate; the sentinel `'__none__'`
   * matches only unestimated tasks (points IS NULL). Omitted when "All story
   * points" is selected.
   */
  points?: number | string;
};

// --- Sessions listing / detail ---

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Sessions-list envelope. Adds `scm_integration_active` — the org-level gate
 * that decides whether the FE renders the Related PRs column at all. Optional
 * at the wire boundary so a FE deploy can precede the backend that emits it.
 */
interface AgentMinerSessionsResponse extends PaginatedResponse<AgentMinerSession> {
  scm_integration_active?: boolean;
}

enum AgentMinerSessionStatus {
  Active = 'ACTIVE',
  Completed = 'COMPLETED',
  Error = 'ERROR',
  Cancelled = 'CANCELLED',
}

enum AgentMinerEventType {
  UserMessage = 'USER_MESSAGE',
  Message = 'MESSAGE',
  Response = 'RESPONSE',
  Thinking = 'THINKING',
  Planning = 'PLANNING',
  PlanningStart = 'PLANNING_START',
  PlanningEnd = 'PLANNING_END',
  ToolCall = 'TOOL_CALL',
  ToolResult = 'TOOL_RESULT',
  ToolError = 'TOOL_ERROR',
  Notification = 'NOTIFICATION',
  PermissionRequest = 'PERMISSION_REQUEST',
  HumanInputRequested = 'HUMAN_INPUT_REQUESTED',
  HumanInputReceived = 'HUMAN_INPUT_RECEIVED',
  Error = 'ERROR',
  ContextCompact = 'CONTEXT_COMPACT',
  SubagentSpawn = 'SUBAGENT_SPAWN',
  SubagentStart = 'SUBAGENT_START',
  SubagentStop = 'SUBAGENT_STOP',
  SubagentError = 'SUBAGENT_ERROR',
  TodoCreated = 'TODO_CREATED',
  TodoCompleted = 'TODO_COMPLETED',
}

enum SessionTaskAssociationType {
  Implements = 'IMPLEMENTS',
  Investigates = 'INVESTIGATES',
  Reviews = 'REVIEWS',
  Tests = 'TESTS',
  Documents = 'DOCUMENTS',
  Refactors = 'REFACTORS',
  Deploys = 'DEPLOYS',
}

interface AgentMinerSessionUser {
  id: string;
  name: string | null;
}

interface AgentMinerSessionLinkedTask {
  id: string;
  task: string;
  task_name: string | null;
  task_code: string | null;
  task_detail: TaskRecord | null;
  association_type: SessionTaskAssociationType | null;
  confidence: number | null;
  source: string | null;
}

enum AgentSessionLabel {
  BugFix = 'bug_fix',
  FeatureBuild = 'feature_build',
  Refactor = 'refactor',
  TestingCoverage = 'testing_coverage',
  Investigation = 'investigation',
  Documentation = 'documentation',
  Devops = 'devops',
  CodeReview = 'code_review',
  Unclassified = 'unclassified',
}

interface FloraAnnotations {
  title?: string | null;
  label?: AgentSessionLabel | string | null;
}

interface AgentMinerSession {
  id: string;
  name: string;
  agent_version: string | null;
  source: AgentSource;
  status: AgentMinerSessionStatus;
  started_at: string | null;
  ended_at: string | null;
  last_turn_at: string | null;
  duration_ms: number | null;
  external_session_id: string | null;
  working_directory: string | null;
  parent_session: string | null;
  user: AgentMinerSessionUser | null;
  organization: string | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cache_creation_tokens: number;
  total_cache_read_tokens: number;
  total_cost_usd: string | null;
  metadata: Record<string, unknown> | null;
  turns_count: number;
  events_count: number;
  has_error: boolean;
  linked_tasks: AgentMinerSessionLinkedTask[];
  flora_annotations: FloraAnnotations | null;
  // Optional at the wire boundary — a FE deploy can land before the backend
  // that emits it, so the sessions-list column must tolerate undefined.
  related_prs?: AgentMinerRelatedPrsSummary;
}

interface AgentMinerEvent {
  id: string;
  event_type: AgentMinerEventType | string;
  sequence: number;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  status: string | null;
  error_type: string | null;
  error_message: string | null;
  tool_name: string | null;
  tool_call_id: string | null;
  // `unknown`, not `Record<string, unknown>`: both are unvalidated JSONFields
  // and the backend stores three shapes, measured across its dev table —
  // tool_input 121,007 object / 2,180 string; tool_output 85,000 object /
  // 26,797 string / 2,952 array. Every array element is an Anthropic content
  // block (`text`, `input_text`, or `image`). Narrow at the use site; the old
  // type let `Object.entries` enumerate a string's characters with no warning
  // from tsc.
  tool_input: unknown;
  tool_output: unknown;
  role: string | null;
  content: string | null;
  child_session: string | null;
  // Hook-specific extras (e.g. THINKING carries `encrypted` + `position`).
  metadata: Record<string, unknown> | null;
  // True when this edit's lines landed in a merged pull request. Optional at
  // the wire boundary for payloads from a backend that does not annotate it.
  wrote_merged_code?: boolean;
}

interface AgentMinerTurn {
  id: string;
  turn_number: number;
  model: string | null;
  effort: string | null;
  response_id: string | null;
  finish_reason: string | null;
  request_params: Record<string, unknown> | null;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  user_prompt: string | null;
  agent_response: string | null;
  events: AgentMinerEvent[];
}

// A subagent child session embedded in its parent's detail payload. One level
// deep — a spawning event's `child_session` id matches one of these.
interface AgentMinerChildSession {
  id: string;
  name: string;
  agent_version: string | null;
  source: AgentSource;
  status: AgentMinerSessionStatus;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  external_session_id: string | null;
  parent_session: string | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cache_creation_tokens: number;
  total_cache_read_tokens: number;
  total_cost_usd: string | null;
  metadata: Record<string, unknown> | null;
  turns: AgentMinerTurn[];
}

interface AgentMinerSessionDetail extends AgentMinerSession {
  turns: AgentMinerTurn[];
  child_sessions: AgentMinerChildSession[];
}

// --- Related PRs (session PR attribution) ---

enum PullRequestState {
  Open = 'open',
  Merged = 'merged',
  Draft = 'draft',
  Closed = 'closed',
}

/** One PR in the sessions-list summary — its number, display state, and repo. */
interface RelatedPrSummaryItem {
  number: number | null;
  state: PullRequestState | string;
  repository_name: string | null;
}

/** Compact per-session PR summary powering the sessions-list "Related PRs" column. */
interface AgentMinerRelatedPrsSummary {
  count: number;
  prs: RelatedPrSummaryItem[];
}

/** Whole-PR authorship rollup (all contributors, not just the viewed session). */
interface WholePrAttribution {
  ai_percentage: number | null;
  total_ai_additions: number;
  total_additions: number;
}

/**
 * Agent-code survival for the viewed session's subtree. `survival_rate` is
 * clamped to [0, 100]; `line_precise` is false when only heuristic/trailer
 * attributions exist, in which case the rate is not meaningful.
 */
interface PullRequestSurvival {
  survived: number;
  authored: number;
  survival_rate: number;
  line_precise: boolean;
}

/** One agent edit that fed a PR file (lineage edge: edit -> file). */
interface PullRequestFileContributingEdit {
  file_edit_id: string;
  event_id: string;
  /** 0-based on the wire; display through `turnDisplayNumber`. */
  turn_number: number | null;
  session_id: string;
  session_title: string | null;
  /** Agent lines from this edit that landed in the PR; null on legacy rows without line evidence. */
  lines_matched: number | null;
}

interface PullRequestFile {
  change_request_file_id: string;
  filename: string | null;
  additions: number;
  deletions: number;
  survived: number;
  authored: number;
  attribution_type: string;
  source: string;
  confidence: number | null;
  // Merged lines written by agents OUTSIDE the viewed session's subtree
  // (deduped, disjoint from `survived`). Optional at the wire boundary so an
  // older/cached payload without it still renders; treat missing as 0.
  agent_other?: number;
  // False for non-line-precise attribution sources (COMMIT_TRAILER / EXPLICIT /
  // MANUAL); when false, `survived` is 0 and a per-file kept% is meaningless.
  // Optional at the wire boundary; treat missing as precise (current rendering).
  line_precise?: boolean;
  // Optional at the wire boundary so a backend without lineage (or a cached
  // response) still renders the file list.
  contributing_edits?: PullRequestFileContributingEdit[];
}

interface ContributingSession {
  session_id: string;
  title: string | null;
  source: AgentSource;
  user: string | null;
  lines: number;
  is_current: boolean;
}

interface SessionPullRequest {
  pr_id: string;
  number: number | null;
  title: string | null;
  state: PullRequestState | string;
  html_url: string | null;
  repository_id: string;
  repository_name: string | null;
  target_branch: string | null;
  additions: number;
  deletions: number;
  changed_files: number;
  created_at: string | null;
  merged_at: string | null;
  whole_pr: WholePrAttribution;
  survival: PullRequestSurvival;
  files: PullRequestFile[];
  contributing_sessions: ContributingSession[];
}

interface SessionPullRequestsResponse {
  pull_requests: SessionPullRequest[];
}

// --- Related PRs (per-file line evidence) ---

enum DiffLineKind {
  Add = 'add',
  Del = 'del',
  Ctx = 'ctx',
}

/** How the matcher credited a merged line to an agent edit. */
enum LineAttributionMatchKind {
  Aligned = 'aligned',
  AnchoredTrivial = 'anchored_trivial',
  Content = 'content',
  Ambiguous = 'ambiguous',
  FileCreated = 'file_created',
  Context = 'context',
}

interface PullRequestLineAttribution {
  file_edit_id: string;
  event_id: string | null;
  /** 0-based on the wire; display through `turnDisplayNumber`. */
  turn_number: number | null;
  session_id: string | null;
  session_title: string | null;
  match_kind: LineAttributionMatchKind | string;
}

interface PullRequestDiffLine {
  kind: DiffLineKind;
  old_line: number | null;
  new_line: number | null;
  content: string;
  /** Only ever set on `kind === 'add'`. */
  attribution: PullRequestLineAttribution | null;
}

interface PullRequestDiffHunk {
  header: string;
  old_start: number;
  new_start: number;
  lines: PullRequestDiffLine[];
}

interface PullRequestAgentEditLine {
  hunk_index: number;
  line_index: number;
  content: string;
  matched_new_line: number | null;
  match_kind: string | null;
  superseded_by: string | null;
}

interface PullRequestAgentEdit {
  file_edit_id: string;
  event_id: string;
  turn_number: number | null;
  session_id: string;
  session_title: string | null;
  /** True when the edit belongs to the viewed session or one of its subagents. */
  is_subtree: boolean;
  tool_name: string | null;
  file_action: string;
  started_at: string | null;
  /** Unique lines this edit wrote to the file. */
  authored: number;
  /** Unique lines of this edit that reached the merged diff. */
  survived: number;
  lines: PullRequestAgentEditLine[];
}

interface PullRequestFileAttributionSummary {
  id: string;
  ai_additions: number;
  ai_deletions: number;
  ai_percentage: number;
  attribution_type: string;
  source: string;
  confidence: number | null;
  /** 0 means a legacy row: no per-line evidence was computed. */
  algorithm_version: number;
  line_evidence_status: string | null;
}

interface PullRequestFileLinesResponse {
  change_request_file_id: string;
  filename: string | null;
  previous_filename: string | null;
  status: string | null;
  additions: number;
  deletions: number;
  patch_available: boolean;
  attribution: PullRequestFileAttributionSummary | null;
  hunks: PullRequestDiffHunk[];
  edits: PullRequestAgentEdit[];
}

interface AgentMinerSessionFacets {
  sources: AgentSource[];
  users: AgentMinerSessionUser[];
  /**
   * Model family display labels (e.g. `"Sonnet 4.5"`) observed on the org's
   * turns. Optional at the wire boundary — a frontend deploy can land before
   * the backend that emits it, so callers must tolerate `undefined`.
   */
  model_families?: string[];
}

interface AgentMinerSessionListParams {
  organization_id: string;
  page?: number;
  page_size?: number;
  q?: string;
  status?: AgentMinerSessionStatus;
  source?: AgentSource[];
  user_id?: string[];
  label?: AgentSessionLabel[];
  start_date?: string;
  end_date?: string;
  has_task?: boolean;
  min_turns?: number;
  /**
   * Model family display labels. Matches a session's DOMINANT family (the one
   * most of its turns used), so a filtered list lines up with the Model
   * Comparison table's per-row session counts.
   */
  model_family?: string[];
  ordering?: string;
}

/** Query params for the model comparison endpoint (snake_case wire format). */
interface ModelComparisonParams {
  organization_id: string;
  start_date: string;
  end_date: string;
  /**
   * Optional bucket size for the chart/trend periods. Omitted lets the backend
   * auto-derive it from the window width; the sidebar filter sets it to an
   * explicit override.
   */
  time_allocation_type?: TimeAllocationType;
}

/** One TimeAllocation bucket echoed back by the endpoint. */
interface ModelComparisonPeriod {
  id: string;
  name: string;
  start: string;
  end: string;
}

/**
 * Response meta block. `cost_visible: false` means the caller lacks the
 * financials_read permission and every cost field in the payload is null.
 */
interface ModelComparisonMeta {
  start_date: string;
  end_date: string;
  confidence_threshold: number;
  weeks_in_window: number;
  /**
   * Effective bucket size (explicit override or the backend's auto-derived
   * default). Optional at the wire boundary — a frontend deploy can land
   * before the backend that emits it.
   */
  time_allocation_type?: TimeAllocationType;
  periods: ModelComparisonPeriod[];
  unpriced_turns: number;
  low_confidence_links_excluded: number;
  cost_visible: boolean;
}

/**
 * KPI strip summary. Rework rate is LOWER-IS-BETTER. Token totals are optional
 * at the wire boundary — a frontend deploy can land before the backend that
 * emits them, matching the `provider?`/`time_allocation_type?` convention.
 */
interface ModelComparisonSummary {
  agentic_sessions: number;
  median_task_cycle_time_days: number | null;
  task_throughput: number;
  rework_rate_pct: number | null;
  total_tokens?: number;
  total_input_tokens?: number;
  total_output_tokens?: number;
  /** Sum of cache-read and cache-creation tokens across all families. */
  total_cache_tokens?: number;
}

/** One line-chart series: weekly median cycle hours keyed by period id. */
interface ModelComparisonSeriesEntry {
  model_family: string;
  values: Record<string, number | null>;
}

/**
 * Per-period token counts for one model family, powering the Usage tab's
 * measure selector (total / input / output / cache).
 */
interface TokenBucket {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_tokens: number;
}

/**
 * One usage-chart series: per-period {@link TokenBucket}s keyed by period id,
 * with null for periods that had no turns (so the chart shows a gap). Buckets
 * are attributed to each turn's own model over the turn's timestamp.
 */
interface ModelComparisonTokenSeriesEntry {
  model_family: string;
  values: Record<string, TokenBucket | null>;
}

/**
 * One comparison table row per model family (sessions from every agent tool
 * merge into the family). Rework rate is LOWER-IS-BETTER; cost fields are
 * null when meta.cost_visible is false.
 */
interface ModelComparisonRow {
  model_family: string;
  sessions: number;
  active_devs: number;
  linked_tasks: number;
  completed_tasks: number;
  rework_rate_pct: number | null;
  median_cycle_time_days: number | null;
  tasks_per_dev_per_week: number | null;
  cost_per_completed_task_usd: number | null;
  total_cost_usd: number | null;
  trend_completed_tasks: number[];
  /**
   * Per-family token totals for the Usage tab table. Optional at the wire
   * boundary (a frontend deploy can precede the backend that emits them) and
   * NOT cost-gated — tokens are not financial data.
   */
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_cache_read_tokens?: number;
  total_cache_creation_tokens?: number;
  total_tokens?: number;
}

/** Vendor that produces a model family, classified server-side from the model id. */
type ModelProvider = 'anthropic' | 'openai' | 'google' | 'xai' | 'deepseek' | 'cursor' | 'unknown';

/**
 * Legend entry: a model family with its observed raw model ids.
 * `provider` is optional at the wire boundary — a frontend deploy can land
 * before the backend that emits it, or the backend can add a provider this
 * union doesn't know about yet. Callers should read it through
 * `normalizeProvider` (model-comparison.type.ts) rather than trusting it directly.
 */
interface ModelComparisonFamily {
  model_family: string;
  raw_models: string[];
  sessions: number;
  provider?: string;
}

/** Full model comparison payload. */
interface ModelComparisonResponse {
  meta: ModelComparisonMeta;
  summary: ModelComparisonSummary;
  cycle_time_series: ModelComparisonSeriesEntry[];
  rows: ModelComparisonRow[];
  families: ModelComparisonFamily[];
  /**
   * Per-model token usage over time for the Usage tab. Optional at the wire
   * boundary — a frontend deploy can land before the backend that emits it.
   */
  token_usage_series?: ModelComparisonTokenSeriesEntry[];
}

/** The canonical dimension values {@link TaskFitDimension} is derived from. */
const TASK_FIT_DIMENSIONS = ['raw_type', 'flora_label'] as const;

/**
 * The dimension the Task Fit matrix rows are grouped by: the raw work-item type
 * from the tracker (`raw_type`) or Flora's classified session label
 * (`flora_label`). Matches the backend `dimension` query param / meta field.
 */
type TaskFitDimension = (typeof TASK_FIT_DIMENSIONS)[number];

/** Query params for the task fit endpoint (snake_case wire format). */
interface TaskFitParams {
  organization_id: string;
  start_date: string;
  end_date: string;
  /** Row grouping dimension; the backend defaults to `raw_type` when omitted. */
  dimension?: TaskFitDimension;
  /**
   * Optional bucket size echoed into `meta.periods`. Omitted lets the backend
   * auto-derive it from the window width.
   */
  time_allocation_type?: TimeAllocationType;
}

/**
 * Response meta block for the task fit endpoint. `cost_visible: false` means the
 * caller lacks the financials_read permission and every cost field is null;
 * `min_cell_tasks` is the sample-size floor below which a cell is suppressed.
 */
interface TaskFitMeta {
  start_date: string;
  end_date: string;
  confidence_threshold: number;
  weeks_in_window: number;
  time_allocation_type: string;
  periods: ModelComparisonPeriod[];
  low_confidence_links_excluded: number;
  dimension: TaskFitDimension;
  min_cell_tasks: number;
  cost_visible: boolean;
}

/**
 * KPI-strip summary for the Task Fit tab. Rework rate is LOWER-IS-BETTER; cycle
 * time and rework are null when the window has no completions.
 */
interface TaskFitSummary {
  agentic_sessions: number;
  linked_tasks: number;
  completed_tasks: number;
  median_task_cycle_time_days: number | null;
  rework_rate_pct: number | null;
}

/**
 * One matrix column: a model family with its session count over the window and
 * the vendor that produces it. `provider` carries a {@link ModelProvider} value
 * classified server-side from the model id; callers should read it through
 * `normalizeProvider` (model-comparison.type.ts) rather than trusting it
 * directly, so an unrecognized future provider collapses to "unknown" instead
 * of leaking a raw wire value into the provider filter.
 */
interface TaskFitColumn {
  model_family: string;
  sessions: number;
  provider: string;
}

/**
 * One matrix cell: the rework rate for a (task type x model family) pair.
 * `rework_rate_pct` is null when there is no shown rate (no data, `sample_size`
 * 0, or `suppressed`). The `_bound_pct` pair is the 95% Wilson score interval
 * on the rework rate (null whenever the rate is null). `suppressed` is true
 * when data exists but falls below `meta.min_cell_tasks`; `is_best` marks the
 * cell the backend deems confidently best in the row (lowest interval upper
 * bound, disjoint from every rival's interval) — the matrix recomputes this
 * client-side over the visible models instead. Cost fields are null when
 * `meta.cost_visible` is false.
 */
interface TaskFitCell {
  model_family: string;
  rework_rate_pct: number | null;
  rework_rate_lower_bound_pct: number | null;
  rework_rate_upper_bound_pct: number | null;
  sample_size: number;
  suppressed: boolean;
  is_best: boolean;
  completed_tasks: number;
  median_cycle_time_days: number | null;
  cost_per_completed_task_usd: number | null;
}

/**
 * One matrix row: a task type (raw type or Flora label) with a dense `cells`
 * array carrying exactly one entry per column, in the same order as
 * {@link TaskFitResponse.columns}. `linked_tasks` counts the row's distinct
 * confidently-linked tasks across the kept columns (optional at the wire
 * boundary — a frontend deploy can land before the backend that emits it).
 */
interface TaskFitRow {
  row_key: string;
  label: string;
  linked_tasks?: number;
  cells: TaskFitCell[];
}

/**
 * A per-row recommendation: the confidently-best model family for a task type
 * and that pairing's headline metrics. Rows whose Wilson intervals overlap
 * produce no recommendation. Cost is null when `meta.cost_visible` is false;
 * the other metrics are null when the pairing has no completions.
 */
interface TaskFitRecommendation {
  row_key: string;
  label: string;
  model_family: string;
  rework_rate_pct: number | null;
  rework_rate_upper_bound_pct: number | null;
  median_cycle_time_days: number | null;
  cost_per_completed_task_usd: number | null;
}

/** Full task fit payload. */
interface TaskFitResponse {
  meta: TaskFitMeta;
  summary: TaskFitSummary;
  columns: TaskFitColumn[];
  rows: TaskFitRow[];
  recommendations: TaskFitRecommendation[];
}

/** Query params for the task fit per-cell tasks drill-down (snake_case wire format). */
interface TaskFitTasksParams {
  organization_id: string;
  start_date: string;
  end_date: string;
  dimension?: TaskFitDimension;
  row_key: string;
  model_family: string;
}

/**
 * One task behind a matrix cell. `code` is the external key parsed from the
 * task URL (e.g. "BFK-123"); `task_type` is the resolved raw-type row key;
 * `reworked` means the task moved backward in the window and `transitioned`
 * that it entered the rework denominator at all.
 */
interface TaskFitTaskItem {
  id: string;
  name: string | null;
  code: string | null;
  url: string | null;
  task_type: string;
  cycle_time_days: number | null;
  transitioned: boolean;
  reworked: boolean;
  doing_date: string | null;
  done_date: string | null;
}

/**
 * The tasks drill-down payload: the cell address echoed back plus counters —
 * `tasks` is capped at `meta.task_cap` entries while `meta.total_tasks` reports
 * the full cell size, so the client can say "showing 200 of 340".
 */
interface TaskFitTasksResponse {
  meta: {
    row_key: string;
    model_family: string;
    dimension: TaskFitDimension;
    start_date: string;
    end_date: string;
    total_tasks: number;
    task_cap: number;
  };
  tasks: TaskFitTaskItem[];
}

/** Query params for the delivery impact endpoint (snake_case wire format). */
interface DeliveryImpactParams {
  organization_id: string;
  start_date: string;
  end_date: string;
  /**
   * Optional bucket size for the cycle-time series periods. Omitted lets the
   * backend auto-derive it from the window width; the sidebar filter sets it
   * to an explicit override.
   */
  time_allocation_type?: TimeAllocationType;
}

/**
 * Response meta block. The contamination counters quantify classification
 * coverage: links dropped below the confidence threshold and tasks flagged
 * used_agent without a confident session link (which land in the baseline).
 */
interface DeliveryImpactMeta {
  start_date: string;
  end_date: string;
  confidence_threshold: number;
  weeks_in_window: number;
  time_allocation_type?: TimeAllocationType;
  periods: ModelComparisonPeriod[];
  low_confidence_links_excluded: number;
  unlinked_agent_flagged_tasks: number;
}

/** Cohort sizes for the window: AI-assisted vs baseline completed tasks. */
interface DeliveryImpactSummary {
  ai_tasks_completed: number;
  baseline_tasks_completed: number;
  ai_share_pct: number | null;
  ai_active_devs: number;
  baseline_active_devs: number;
}

/**
 * One comparison-card metric block. `delta_pct` is backend-computed as
 * (ai - baseline) / baseline * 100 and null when either side is missing.
 * The per-side counts are the metric's qualifying "n=" (tasks, or merged PRs
 * for time-to-merge), not the full cohort sizes.
 */
interface DeliveryImpactMetric {
  ai: number | null;
  baseline: number | null;
  ai_task_count: number;
  baseline_task_count: number;
  delta_pct: number | null;
}

/** The six comparison-card metric blocks keyed by metric name. */
interface DeliveryImpactMetrics {
  median_cycle_time_days: DeliveryImpactMetric;
  median_lead_time_days: DeliveryImpactMetric;
  throughput_per_dev_per_week: DeliveryImpactMetric;
  rework_rate_pct: DeliveryImpactMetric;
  median_review_to_done_days: DeliveryImpactMetric;
  median_time_to_merge_days: DeliveryImpactMetric;
}

/**
 * One cohort's cycle-time series: per-period medians and task counts keyed by
 * period id, with null for future periods ("no data yet").
 */
interface DeliveryImpactCohortSeries {
  median_days: Record<string, number | null>;
  task_count: Record<string, number | null>;
}

/** Full delivery impact payload. */
interface DeliveryImpactResponse {
  meta: DeliveryImpactMeta;
  summary: DeliveryImpactSummary;
  metrics: DeliveryImpactMetrics;
  cycle_time_series: {
    ai: DeliveryImpactCohortSeries;
    baseline: DeliveryImpactCohortSeries;
  };
}

/**
 * Attribution tier a session was resolved to a team through, strongest first.
 * `unattributed` sessions matched no signal and are held out of every team row.
 */
type AgentAdoptionTierKey =
  | 'task_linked'
  | 'branch_inferred'
  | 'directory_inferred'
  | 'member_inferred'
  | 'unattributed';

/** Query params for the team adoption endpoint (snake_case wire format). */
interface AgentAdoptionParams {
  organization_id: string;
  start_date: string;
  end_date: string;
  /**
   * Selected portfolio; scopes the team universe to that portfolio's
   * projects, matching the team list the filter panel offers.
   */
  portfolio_id?: string;
  /**
   * Optional bucket size for the trend periods. Omitted lets the backend
   * auto-derive it from the window width; the sidebar filter sets it to an
   * explicit override.
   */
  time_allocation_type?: TimeAllocationType;
  /** Optional team (project) scope; omitted means every team the caller may view. */
  team_ids?: string[];
}

/**
 * Response meta block. Extends the shared analytics meta with the echoed team
 * scope and the ordered attribution tier names.
 */
interface AgentAdoptionMeta {
  start_date: string;
  end_date: string;
  confidence_threshold: number;
  weeks_in_window: number;
  /** Optional at the wire boundary — a frontend deploy can precede the backend. */
  time_allocation_type?: TimeAllocationType;
  periods: ModelComparisonPeriod[];
  low_confidence_links_excluded: number;
  team_ids: string[];
  attribution_tiers: AgentAdoptionTierKey[];
}

/**
 * Share of sessions per attribution outcome, backend-computed percentages.
 * All null when the window has no sessions.
 */
interface AgentAdoptionCoverage {
  team_resolved_rate: number | null;
  work_linked_rate: number | null;
  member_inferred_rate: number | null;
  unattributed_rate: number | null;
}

/**
 * Window-level KPI summary. `adoption_rate` uses the canonical formula shared
 * with the dashboard: active agent users ÷ (active ∪ in-window task assignees).
 */
interface AgentAdoptionSummary {
  active_agent_users: number;
  eligible_users: number;
  adoption_rate: number;
  total_sessions: number;
  total_turns: number;
  avg_turns_per_session: number | null;
  median_turns_per_session: number | null;
  total_agent_file_edits: number;
  attribution_coverage: AgentAdoptionCoverage;
}

/** Org- or team-level period-keyed series; null values are future periods. */
interface AgentAdoptionOverTime {
  active_users: MeasureValues;
  sessions: MeasureValues;
  turns: MeasureValues;
  agent_file_edits: MeasureValues;
  avg_turns_per_session: MeasureValues;
  adoption_rate: MeasureValues;
}

/**
 * Session counts per work/member tier for one team. Tiers 1-3 fan a session
 * out to every linked team, so summing these across teams can exceed the org
 * session total — never sum team rows.
 */
interface AgentAdoptionTeamAttribution {
  task_linked: number;
  branch_inferred: number;
  directory_inferred: number;
  member_inferred: number;
}

/** One team's window summary. */
interface AgentAdoptionTeamSummary {
  active_agent_users: number;
  eligible_users: number;
  adoption_rate: number;
  sessions: number;
  turns: number;
  avg_turns_per_session: number | null;
  agent_file_edits: number;
  attribution: AgentAdoptionTeamAttribution;
}

/** One Teams-scorecard row: window summary plus per-period series. */
interface AgentAdoptionTeamRow {
  id: string;
  name: string;
  summary: AgentAdoptionTeamSummary;
  over_time: AgentAdoptionOverTime;
}

/**
 * Org-level tier breakdown. Every session counts exactly once under its
 * winning tier, so the tier counts sum to `total_sessions`.
 */
interface AgentAdoptionAttributionBreakdown {
  total_sessions: number;
  tiers: Record<AgentAdoptionTierKey, number>;
  diagnostics: {
    ambiguous_branch_sessions: number;
    ambiguous_directory_sessions: number;
    ambiguous_member_sessions: number;
  };
}

/** Per-source totals for sessions no tier could map to a team. */
interface AgentAdoptionUnattributedSourceStats {
  sessions: number;
  turns: number;
  agent_file_edits: number;
  avg_turns_per_session: number | null;
  active_ai_users: number;
}

/** Totals for the sessions no tier could map to a team. */
interface AgentAdoptionUnattributedSummary {
  total_sessions: number;
  total_turns: number;
  total_agent_file_edits: number;
  avg_turns_per_session: number | null;
  active_ai_users: number;
  by_source: Record<string, AgentAdoptionUnattributedSourceStats>;
}

/** Full team adoption payload. */
interface AgentAdoptionResponse {
  meta: AgentAdoptionMeta;
  summary: AgentAdoptionSummary;
  over_time: AgentAdoptionOverTime;
  teams: AgentAdoptionTeamRow[];
  attribution_breakdown: AgentAdoptionAttributionBreakdown;
  unattributed_summary: AgentAdoptionUnattributedSummary;
}

export {
  AgentMinerSessionStatus,
  AgentSessionLabel,
  DiffLineKind,
  LineAttributionMatchKind,
  PullRequestState,
  TASK_FIT_DIMENSIONS,
};
export type {
  AgentMinerRelatedPrsSummary,
  AgentMinerSessionsResponse,
  ContributingSession,
  PullRequestAgentEdit,
  PullRequestAgentEditLine,
  PullRequestDiffHunk,
  PullRequestDiffLine,
  PullRequestFile,
  PullRequestFileAttributionSummary,
  PullRequestFileContributingEdit,
  PullRequestFileLinesResponse,
  PullRequestLineAttribution,
  PullRequestSurvival,
  RelatedPrSummaryItem,
  SessionPullRequest,
  SessionPullRequestsResponse,
  WholePrAttribution,
  AgentAdoptionAttributionBreakdown,
  AgentAdoptionCoverage,
  AgentAdoptionMeta,
  AgentAdoptionOverTime,
  AgentAdoptionParams,
  AgentAdoptionResponse,
  AgentAdoptionSummary,
  AgentAdoptionTeamAttribution,
  AgentAdoptionTeamRow,
  AgentAdoptionTeamSummary,
  AgentAdoptionTierKey,
  AgentAdoptionUnattributedSourceStats,
  AgentAdoptionUnattributedSummary,
  AgentDashboardParams,
  AgentDashboardResponse,
  AgentMinerChildSession,
  AgentMinerEvent,
  AgentMinerSession,
  AgentMinerSessionDetail,
  AgentMinerSessionFacets,
  AgentMinerSessionLinkedTask,
  AgentMinerSessionListParams,
  AgentMinerTurn,
  CycleTimeHistogramBucket,
  DeliveryImpactCohortSeries,
  DeliveryImpactMeta,
  DeliveryImpactMetric,
  DeliveryImpactMetrics,
  DeliveryImpactParams,
  DeliveryImpactResponse,
  DeliveryImpactSummary,
  MeasureValues,
  ModelComparisonFamily,
  ModelComparisonMeta,
  ModelComparisonParams,
  ModelComparisonPeriod,
  ModelComparisonResponse,
  ModelComparisonRow,
  ModelComparisonSeriesEntry,
  ModelComparisonSummary,
  ModelComparisonTokenSeriesEntry,
  ModelProvider,
  PaginatedResponse,
  TaskFitCell,
  TaskFitColumn,
  TaskFitDimension,
  TaskFitMeta,
  TaskFitParams,
  TaskFitRecommendation,
  TaskFitResponse,
  TaskFitRow,
  TaskFitSummary,
  TaskFitTaskItem,
  TaskFitTasksParams,
  TaskFitTasksResponse,
  TokenBucket,
};
